import React, { useEffect, useState } from 'react';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import SettingsSidebarAd from "../Sidebars/SettingsSidebarAd";
import Modal from "./Modal";
import { useTranslation } from 'react-i18next';

type Prefs = {
  analyticsConsent: boolean;
  locationConsent: boolean;
  pushConsent: boolean;
};

const defaultPrefs: Prefs = {
  analyticsConsent: true,
  locationConsent: false,
  pushConsent: false,
};

/* Reusable green/red toggle like FirstRunPrivacy */
const ToggleRow: React.FC<{
  title: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ title, description, checked, onChange, disabled }) => {
  const { t } = useTranslation();
  return (
    <div style={rowWrap}>
      <div style={leftCol}>
        <label style={{ fontWeight: 600 }}>{title}</label>
        {description ? <div style={hint}>{description}</div> : null}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        disabled={disabled}
        aria-pressed={checked}
        style={{
          ...pillBtn,
          backgroundColor: checked ? '#10B981' : '#EF4444',
          opacity: disabled ? 0.6 : 1,
          minWidth: 90,
        }}
      >
        {checked
          ? t('dataPrivacy.optional.toggles.yes')
          : t('dataPrivacy.optional.toggles.no')}
      </button>
    </div>
  );
};


const DataPrivacySettings: React.FC = () => {
  const { logout } = useAuth();
  const { t } = useTranslation();

  const [prefs, setPrefs] = useState<Prefs>(defaultPrefs);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  // deletion flow
  const [showPwModal, setShowPwModal] = useState(false);
  const [confirmPw, setConfirmPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState('');
  const [otpError, setOtpError] = useState('');
  
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  const [pwModalKey, setPwModalKey] = useState(0);
  const [pwFieldName, setPwFieldName] = useState('delete-password');
  const [pwReadOnly, setPwReadOnly] = useState(true);

  // Load existing preferences
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/privacy/preferences');
        const loaded: Prefs = { ...defaultPrefs, ...data };
        setPrefs(loaded);
        if (data?.updatedAt) setLastSavedAt(new Date(data.updatedAt).toLocaleString());
      } catch {
        // keep defaults
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Single saver used by all toggles (auto-save)
  const savePrefs = async (next: Prefs) => {
    setSaving(true);
    try {
      const { data } = await api.put('/privacy/preferences', next);

      setPrefs(prev => ({ ...prev, ...next }));
      setLastSavedAt(new Date(data?.updatedAt || Date.now()).toLocaleString());
    } catch (e) {
      console.error(e);
      alert(t('dataPrivacy.optional.alerts.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // Handlers for each toggle
  const setAnalytics = (v: boolean) => savePrefs({ ...prefs, analyticsConsent: v });
  const setPush = (v: boolean) => savePrefs({ ...prefs, pushConsent: v });
  const setLocation = (v: boolean) => savePrefs({ ...prefs, locationConsent: v });

  // Deleting Data
  const openDeletion = () => {
    setConfirmPw('');
    setOtp('');
    setPwError('');
    setOtpError('');
  
    setPwModalKey(k => k + 1);
    setPwFieldName(`delete-password-${Date.now()}`);
    setPwReadOnly(true);
    setShowPwModal(true);
    setTimeout(() => setPwReadOnly(false), 50);
  };
  

  const closePwModal = () => {
    setConfirmPw('');
    setPwError('');
    setShowPwModal(false);
  };
  const requestDeletion = async () => {
    setPwError('');
    setOtpError('');
  
    if (!confirmPw) {
      setPwError(t('dataPrivacy.deletion.alerts.missingPassword'));
      return;
    }
  
    try {
      setPwBusy(true);
      await api.post('/privacy/delete-account/request', { password: confirmPw });
 
      setConfirmPw('');
      setShowPwModal(false);
      setShowOtpModal(true);
    } catch (e: any) {
      const msg = e?.response?.data?.msg || t('dataPrivacy.deletion.alerts.startError');
      setPwError(String(msg)); // ✅ show under password input
    } finally {
      setPwBusy(false);
    }
  };
  
  const verifyDeletion = async () => {
    setOtpError('');
  
    if (!otp || otp.length !== 6) {
      setOtpError(t('dataPrivacy.deletion.alerts.missingOtp'));
      return;
    }
  
    try {
      setOtpBusy(true);
  
      await api.post('/privacy/delete-account/verify', { otp });

      // ✅ When OTP is correct: no alert, just logout + redirect
      await logout();
      window.location.href = '/';
    } catch (e: any) {
      const msg = e?.response?.data?.msg || t('dataPrivacy.deletion.alerts.verifyError');
      setOtpError(String(msg)); // ✅ show under OTP input
    } finally {
      setOtpBusy(false);
    }
  };
  

const handleExport = async () => {
  try {
    const res = await api.get('/privacy/export', { responseType: 'blob' });

    const blob = new Blob([res.data], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unydo-export-${new Date().toISOString().slice(0,10)}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    console.error(e);
    alert(t('dataPrivacy.export.alerts.exportError'));
  }
};

return (
  <div style={{ display: 'flex', minHeight: '100vh', background: '#02040b' }}>
    {/* Left fixed settings sidebar */}
    <SettingsSidebarAd />

    {/* Main content */}
    <main
      style={{
        marginLeft: 292,
        padding: '45px 38px 24px 32px',
        width: '100%',
        boxSizing: 'border-box',
        color: '#e5e7eb',
      }}
    >
      <form
        style={{
          width: '100%',
          maxWidth: 960,
        }}
        onSubmit={(e) => e.preventDefault()}
      >
        <h2 style={{ marginTop: 0, fontSize: 24, fontWeight: 700 }}>
          {t('dataPrivacy.page.title')}
        </h2>
        <p style={{ ...hint, marginTop: 4, marginBottom: 12 }}>
          {t('dataPrivacy.page.subtitle')}
        </p>

        {/* Intro / legal text */}
        <section
          style={{
            borderRadius: 12,
            border: '1px solid rgba(148,163,184,0.4)',
            background: 'rgba(15,23,42,0.9)',
            padding: 16,
            marginBottom: 16,
          }}
        >
          <div style={{ ...hint, marginTop: 0, marginBottom: 6 }}>
            {t('dataPrivacy.intro.mainText')}
          </div>
          <div style={{ ...hint }}>
            {t('dataPrivacy.intro.legalSeeOur')}{' '}
            <a
              href="/legal/privacy#privacy"
              target="_blank"
              rel="noreferrer"
              style={{ color: '#93c5fd' }}
            >
              {t('dataPrivacy.intro.privacyLinkLabel')}
            </a>{' '}
            {t('dataPrivacy.intro.and')}{' '}
            <a
              href="/legal/privacy#subprocessors"
              target="_blank"
              rel="noreferrer"
              style={{ color: '#93c5fd' }}
            >
              {t('dataPrivacy.intro.subprocessorsLinkLabel')}
            </a>
            .
          </div>
        </section>

        {/* Essential (read-only) */}
        <section
          style={{
            borderRadius: 12,
            border: '1px solid rgba(148,163,184,0.4)',
            background: 'rgba(15,23,42,0.9)',
            padding: 16,
            marginBottom: 16,
          }}
        >
          <div style={rowWrap}>
            <div style={leftCol}>
              <label style={{ fontWeight: 600, fontSize: 14 }}>
                {t('dataPrivacy.essential.title')}
              </label>
              <div style={hint}>
                {t('dataPrivacy.essential.description')}
              </div>
            </div>
            <button
              type="button"
              disabled
              style={{
                ...pillBtn,
                backgroundColor: '#6b7280',
                cursor: 'not-allowed',
              }}
            >
              {t('dataPrivacy.essential.badgeLabel')}
            </button>
          </div>
        </section>

        {/* Optional consents */}
        <section
          style={{
            borderRadius: 12,
            border: '1px solid rgba(148,163,184,0.4)',
            background: 'rgba(15,23,42,0.9)',
            padding: 16,
            marginBottom: 16,
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
            {t('dataPrivacy.optional.sectionTitle')}
          </h3>

          {loading ? (
            <p style={{ ...hint, marginTop: 4 }}>
              {t('dataPrivacy.optional.loading')}
            </p>
          ) : (
            <>
              <ToggleRow
                title={t('dataPrivacy.optional.toggles.analyticsTitle')}
                description={t('dataPrivacy.optional.toggles.analyticsDescription')}
                checked={prefs.analyticsConsent}
                onChange={setAnalytics}
                disabled={saving}
              />
              <ToggleRow
                title={t('dataPrivacy.optional.toggles.pushTitle')}
                description={t('dataPrivacy.optional.toggles.pushDescription')}
                checked={prefs.pushConsent}
                onChange={setPush}
                disabled={saving}
              />
              <ToggleRow
                title={t('dataPrivacy.optional.toggles.locationTitle')}
                description={t('dataPrivacy.optional.toggles.locationDescription')}
                checked={prefs.locationConsent}
                onChange={setLocation}
                disabled={saving}
              />

              {lastSavedAt && (
                <div style={{ ...hint, marginTop: 4 }}>
                  {saving
                    ? t('dataPrivacy.optional.saving')
                    : t('dataPrivacy.optional.lastSaved', {
                        timestamp: lastSavedAt,
                      })}
                </div>
              )}
            </>
          )}
        </section>

        {/* Hard delete */}
        <section
          style={{
            borderRadius: 12,
            border: '1px solid rgba(239,68,68,0.5)',
            background: '#1f2933',
            padding: 16,
            marginBottom: 16,
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
            {t('dataPrivacy.deletion.sectionTitle')}
          </h3>
          <p style={{ color: '#fca5a5', fontSize: 13, marginBottom: 4 }}>
            {t('dataPrivacy.deletion.warnLine1')}
          </p>
          <p style={{ color: '#fca5a5', fontSize: 13, marginTop: 0, marginBottom: 10 }}>
            {t('dataPrivacy.deletion.warnLine2')}
          </p>
          <button
            type="button"
            onClick={openDeletion}
            style={{
              ...buttonStyle,
              background: '#b91c1c',
            }}
          >
            {t('dataPrivacy.deletion.buttonLabel')}
          </button>
        </section>

        {/* Data rights: export */}
        <section
          style={{
            borderRadius: 12,
            border: '1px solid rgba(148,163,184,0.4)',
            background: 'rgba(15,23,42,0.9)',
            padding: 16,
            marginBottom: 16,
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
            {t('dataPrivacy.export.sectionTitle')}
          </h3>
          <p style={{ ...hint, marginTop: 0, marginBottom: 10 }}>
            {t('dataPrivacy.export.description')}
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleExport}
              style={{
                ...pillBtn,
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              {t('dataPrivacy.export.buttonLabel')}
            </button>
          </div>
        </section>

        {/* Privacy policy link */}
        <section
          style={{
            borderRadius: 12,
            border: '1px solid rgba(148,163,184,0.4)',
            background: 'rgba(15,23,42,0.9)',
            padding: 16,
            marginBottom: 32,
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
            {t('dataPrivacy.policySection.sectionTitle')}
          </h3>
          <p style={{ ...hint, marginTop: 0, marginBottom: 10 }}>
            {t('dataPrivacy.policySection.description')}
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <a
              href="/legal/privacy#privacy"
              target="_blank"
              rel="noreferrer"
              style={{
                ...pillBtn,
                textDecoration: 'none',
                display: 'inline-block',
                backgroundColor: '#111827',
              }}
            >
              {t('dataPrivacy.policySection.buttonLabel')}
            </a>
          </div>
        </section>
      </form>
    </main>

    {/* Password modal */}
    <Modal
      open={showPwModal}
      onClose={() => {
        if (!pwBusy) closePwModal();
      }}
      title={t('dataPrivacy.deletion.pwModalTitle')}
      width={420}
    >
      <>
        <p id="delete-confirm-desc" style={{ marginTop: 6 }}>
        {t('dataPrivacy.deletion.pwModalDescription')}
        </p>

        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          {/* hidden fields for password managers */}
          <input type="text" name="username" autoComplete="username" style={{ display: 'none' }} />
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            style={{ display: 'none' }}
          />

<input
  key={pwModalKey}
  type="password"
  placeholder={t('dataPrivacy.deletion.pwPlaceholder')}
  value={confirmPw}
  onChange={(e) => {
    setConfirmPw(e.target.value);
    setPwError('');
  }}
  onKeyDown={(e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!pwBusy && confirmPw) requestDeletion();
    }
  }}
  style={inputStyle}
  autoFocus
  name={pwFieldName}
  autoComplete="new-password"
  data-lpignore="true"
  readOnly={pwReadOnly}
  onFocus={() => pwReadOnly && setPwReadOnly(false)}
/>

{!!pwError && (
  <div style={{ color: '#c0392b', marginTop: 8 }}>
    {pwError}
  </div>
)}

        </form>

        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
  <button
    type="button"
    onClick={closePwModal}
    disabled={pwBusy}
    style={{ ...buttonStyle, background: '#6b7280' }}
  >
    {t('dataPrivacy.deletion.pwCancel')}
  </button>

  <button
    type="button"
    onClick={requestDeletion}
    disabled={!confirmPw || pwBusy}
    style={{
      ...buttonStyle,
      background: '#2563eb',
      opacity: !confirmPw || pwBusy ? 0.6 : 1,
    }}
  >
    {pwBusy
      ? t('dataPrivacy.deletion.pwSendCodeBusy')
      : t('dataPrivacy.deletion.pwSendCodeIdle')}
  </button>
</div>

      </>
    </Modal>

    {/* OTP modal */}
    <Modal
      open={showOtpModal}
      onClose={() => {
        if (!otpBusy) setShowOtpModal(false);
      }}
      title={t('dataPrivacy.deletion.otpModalTitle')}
      width={360}
      closeOnOverlayClick={false}
    >
      <>
        <p style={{ color: '#fca5a5' }}>
        {t('dataPrivacy.deletion.otpModalDescription')}
        </p>

        <input
  inputMode="numeric"
  pattern="\d*"
  maxLength={6}
  value={otp}
  onChange={(e) => {
    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
    setOtpError('');
  }}
  onKeyDown={(e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!otpBusy && otp.length === 6) verifyDeletion();
    }
  }}
  placeholder={t('dataPrivacy.deletion.otpPlaceholder')}
  style={inputStyle}
/>
{!!otpError && (
  <div style={{ color: '#c0392b', marginTop: 8 }}>
    {otpError}
  </div>
)}


<div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
  <button
    type="button"
    onClick={() => setShowOtpModal(false)}
    disabled={otpBusy}
    style={{ ...buttonStyle, background: '#6b7280' }}
  >
    {t('common.cancel')}
  </button>

  <button
    type="button"
    onClick={verifyDeletion}
    disabled={otp.length !== 6 || otpBusy}
    style={{
      ...buttonStyle,
      background: '#b91c1c',
      opacity: otp.length !== 6 || otpBusy ? 0.6 : 1,
    }}
  >
    {otpBusy
      ? t('dataPrivacy.deletion.otpDeleteBusy')
      : t('dataPrivacy.deletion.otpDeleteIdle')}
  </button>
</div>

      </>
    </Modal>
  </div>
);


};

/* styles */
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px',
  marginTop: 6,
  borderRadius: 6,
  border: '1px solid rgba(148,163,184,0.6)',
  background: '#020617',
  color: '#e5e7eb',
  boxSizing: 'border-box',
};

const buttonStyle: React.CSSProperties = {
  height: 40,
  padding: '0 14px',
  borderRadius: 8,
  border: 'none',
  color: '#fff',
  background: '#2563eb',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,

  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
};


const rowWrap: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-end',
  marginBottom: 18,
};
const leftCol: React.CSSProperties = { flex: 1 };
const pillBtn: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: '#111827',
  color: '#e5e7eb',
  border: '1px solid rgba(148,163,184,0.6)',
  borderRadius: 999,
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 13,
};
const hint: React.CSSProperties = {
  color: '#9ca3af',
  fontSize: 12,
  marginTop: 6,
};const sectionHr: React.CSSProperties = { margin: '24px 0' };

export default DataPrivacySettings;